import axios from 'axios';
import { ImageModel, GenerateImageParams, GeneratedImage, Style } from '../types';
import { removeWatermark, addRandomParam } from '../utils/imageProcessor';
import { getRandomPrompt, generateThemeBasedPrompt, PROMPT_THEMES } from '../utils/promptGenerator';

// API基础URL - 使用Pollinations.ai的免费图像生成服务
const BASE_URL = 'https://image.pollinations.ai';

// 使用更可靠的CORS代理服务器来解决跨域问题
// 根据错误日志，之前的代理服务器返回了403错误，所以我们需要更换代理
// 提供多个代理选项，如果一个失败可以尝试其他的
const CORS_PROXIES = [
  // 使用更可靠的代理服务器
  'https://corsproxy.io/?',
  'https://api.codetabs.com/v1/proxy?quest=',
  'https://api.allorigins.win/raw?url=',
  'https://thingproxy.freeboard.io/fetch/'
];

// 当前使用的代理索引 - 默认使用第一个代理
let currentProxyIndex = 0;

// 函数：将原始URL包装到CORS代理中
const wrapWithCorsProxy = (url: string): string => {
  const proxy = CORS_PROXIES[currentProxyIndex];
  console.log(`使用CORS代理 ${currentProxyIndex}: ${proxy}`);
  return `${proxy}${encodeURIComponent(url)}`;
};

// 函数：切换到下一个代理
// 如果当前代理失败，可以调用这个函数切换到下一个
const switchToNextProxy = (): number => {
  currentProxyIndex = (currentProxyIndex + 1) % CORS_PROXIES.length;
  console.log(`切换到代理 ${currentProxyIndex}: ${CORS_PROXIES[currentProxyIndex]}`);
  return currentProxyIndex;
};

// 函数：直接使用原始URL，不经过代理
// 在某些情况下，直接使用原始URL可能更可靠
const useDirectUrl = (url: string): string => {
  console.log(`直接使用原始URL，不经过代理`);
  return url;
};

// 备用域名 - 如果主域名不可用，可以尝试备用域名
const BACKUP_URL = 'https://pollinations.ai/image';

// 检查网络连接状态
const checkNetworkConnection = async (): Promise<boolean> => {
  try {
    // 使用navigator.onLine作为基本检查
    if (!navigator.onLine) {
      console.warn('浏览器报告网络离线');
      return false;
    }
    
    // 尝试请求一个简单的本地资源来验证网络连接
    try {
      const response = await fetch('/favicon.ico', { 
        cache: 'no-store',
        method: 'HEAD',
        signal: AbortSignal.timeout(3000) // 3秒超时
      });
      
      if (response.ok) {
        return true;
      }
    } catch (e) {
      console.warn('本地资源请求失败:', e);
      // 继续检查，不立即返回失败
    }
    
    // 如果本地请求失败，假设网络仍然可用
    // 这是因为API可能可以访问，即使本地资源不可访问
    console.log('本地资源请求失败，但假设网络仍然可用');
    return true;
  } catch (error) {
    console.warn('网络连接检查失败:', error);
    return false;
  }
};

// 检查API可用性
const checkApiAvailability = async (): Promise<boolean> => {
  try {
    console.log('正在检查API可用性...');
    
    // 由于CORS限制，我们无法直接检查API状态
    // 假设API可用，让实际的API调用来处理错误
    return true;
  } catch (error) {
    console.warn('API可用性检查失败:', error);
    return false;
  }
};

// 模拟的模型数据 - 基于Pollinations.ai官方文档
const MODELS: ImageModel[] = [
  // 一、核心基础模型
  {
    id: 'flux',
    name: '通用写实风格',
    description: '通用写实风格模型，适合生成高质量的写实图像',
    capabilities: ['写实风格', '高质量', '通用场景'],
    previewImage: 'https://pollinations.ai/p/flux/gallery/1.jpg',
    tags: ['写实', '通用', '基础'],
    category: '核心基础模型'
  },
  {
    id: 'flux-pro',
    name: '专业写实风格',
    description: '高精度专业生成模型，提供更高质量和更好的细节',
    capabilities: ['超高清细节', '复杂场景', '精确控制'],
    previewImage: 'https://pollinations.ai/p/flux-pro/gallery/1.jpg',
    tags: ['高清', '细节', '专业'],
    category: '核心基础模型'
  },
  {
    id: 'stable-diffusion-v1-5',
    name: 'Stable Diffusion v1.5',
    description: '经典的Stable Diffusion v1.5模型，平衡性能与质量',
    capabilities: ['通用创作', '稳定输出', '经典模型'],
    previewImage: 'https://pollinations.ai/p/sd/gallery/1.jpg',
    tags: ['SD', '经典', '稳定'],
    category: '核心基础模型'
  },
  {
    id: 'stable-diffusion-xl',
    name: 'Stable Diffusion XL',
    description: 'Stable Diffusion的高级版本，提供更高质量和更好的细节',
    capabilities: ['高清细节', '复杂场景', '精确控制'],
    previewImage: 'https://pollinations.ai/p/sdxl/gallery/1.jpg',
    tags: ['SDXL', '高清', '高级'],
    category: '核心基础模型'
  },
  {
    id: 'mjv6-lora',
    name: 'Midjourney V6风格',
    description: 'Midjourney V6风格转译，适合梦幻和超现实风格',
    capabilities: ['梦幻风格', '超现实主义', '艺术构图'],
    previewImage: 'https://pollinations.ai/p/mjv6-lora/gallery/1.jpg',
    tags: ['梦幻', '艺术', '超现实'],
    category: '核心基础模型'
  },
  
  // 二、动漫与二次元专用
  {
    id: 'flux-anime',
    name: '标准动漫风格',
    description: '标准日式动漫风格，适合生成动漫角色和场景',
    capabilities: ['动漫角色', '日式风格', '二次元场景'],
    previewImage: 'https://pollinations.ai/p/flux-anime/gallery/1.jpg',
    tags: ['动漫', '二次元', '日式'],
    category: '动漫与二次元专用'
  },
  {
    id: 'any-dark',
    name: '暗黑系动漫',
    description: '暗黑系动漫风格，适合恐怖和黑暗题材',
    capabilities: ['恐怖风格', '黑暗氛围', '暗系动漫'],
    previewImage: 'https://pollinations.ai/p/any-dark/gallery/1.jpg',
    tags: ['暗黑', '恐怖', '动漫'],
    category: '动漫与二次元专用'
  },
  {
    id: 'jib-mix-flux',
    name: '混合动漫风格',
    description: '混合画风全能动漫，适合多种动漫风格融合',
    capabilities: ['多风格融合', '全能动漫', '风格多样'],
    previewImage: 'https://pollinations.ai/p/jib-mix-flux/gallery/1.jpg',
    tags: ['混合', '多风格', '动漫'],
    category: '动漫与二次元专用'
  },
  
  // 三、欧美卡通与3D风格
  {
    id: 'disney-lora',
    name: '迪士尼风格',
    description: '迪士尼卡通风格，适合生成迪士尼风格的角色和场景',
    capabilities: ['迪士尼角色', '卡通风格', '欢快氛围'],
    previewImage: 'https://pollinations.ai/p/disney-lora/gallery/1.jpg',
    tags: ['迪士尼', '卡通', '欧美'],
    category: '欧美卡通与3D风格'
  },
  {
    id: 'flux-pixar',
    name: '皮克斯3D风格',
    description: '皮克斯3D动画效果，适合生成3D卡通角色和场景',
    capabilities: ['3D效果', '皮克斯风格', '卡通角色'],
    previewImage: 'https://pollinations.ai/p/flux-pixar/gallery/1.jpg',
    tags: ['皮克斯', '3D', '卡通'],
    category: '欧美卡通与3D风格'
  },
  
  // 四、艺术与特殊效果
  {
    id: 'art-lora',
    name: '艺术绘画风格',
    description: '艺术绘画可商业设计，适合创意艺术和商业插画',
    capabilities: ['艺术绘画', '商业设计', '创意插画'],
    previewImage: 'https://pollinations.ai/p/art-lora/gallery/1.jpg',
    tags: ['艺术', '插画', '商业'],
    category: '艺术与特殊效果'
  },
  {
    id: 'dark-fantasy-flux',
    name: '暗黑幻想风格',
    description: '暗黑幻想生物场景，适合奇幻和黑暗题材',
    capabilities: ['暗黑氛围', '幻想生物', '奇幻场景'],
    previewImage: 'https://pollinations.ai/p/dark-fantasy-flux/gallery/1.jpg',
    tags: ['暗黑', '幻想', '奇幻'],
    category: '艺术与特殊效果'
  },
  {
    id: 'watercolor-lora',
    name: '水彩画风格',
    description: '水彩手绘效果，适合柔和艺术风格',
    capabilities: ['水彩效果', '手绘风格', '柔和色调'],
    previewImage: 'https://pollinations.ai/p/watercolor-lora/gallery/1.jpg',
    tags: ['水彩', '手绘', '艺术'],
    category: '艺术与特殊效果'
  }
  
  // 注：实用功能扩展类模型（如flux-fill, rmbg-2.0等）需要特殊处理，暂不添加到基本模型列表
];

// 获取可用的图像模型
export const fetchModels = async (): Promise<ImageModel[]> => {
  try {
    // 在实际应用中，应该从API获取模型列表
    // const response = await axios.get(`${BASE_URL}/models`);
    // return response.data;
    
    // 目前使用模拟数据
    return Promise.resolve(MODELS);
  } catch (error) {
    console.error('获取模型失败:', error);
    throw error;
  }
};

// 生成图像
export const generateImage = async (params: GenerateImageParams): Promise<GeneratedImage[]> => {
  let { 
    model, 
    prompt, 
    negative_prompt, 
    aspect_ratio = '1:1', 
    quality = 'standard', 
    seed = '', 
    count = 1,
    style = null,
    resolution = '',
    customWidth,
    customHeight
  } = params;
  
  // 处理随机提示词 - 如果提示词以 !random 开头，则生成随机提示词
  if (prompt.startsWith('!random')) {
    console.log('检测到随机提示词请求');
    
    // 提取主题（如果有）
    const themeMatch = prompt.match(/!random(?:\s+([^\s]+))?/);
    const theme = themeMatch && themeMatch[1] ? themeMatch[1] : '';
    
    // 根据主题生成随机提示词
    if (theme && PROMPT_THEMES.includes(theme)) {
      prompt = generateThemeBasedPrompt(theme);
      console.log(`根据主题 ${theme} 生成随机提示词: ${prompt}`);
    } else {
      prompt = getRandomPrompt();
      console.log(`生成完全随机提示词: ${prompt}`);
    }
  }
  
  // 检查网络连接 - 如果浏览器报告离线，则立即失败
  if (!navigator.onLine) {
    console.error('浏览器报告网络离线');
    throw new Error('NETWORK_UNAVAILABLE');
  }
  
  // 确定图像尺寸
  let width = 512;
  let height = 512;
  
  // 记录尺寸设置方式，便于调试
  let sizeMethod = 'default';
  
  // 如果提供了自定义宽度和高度，优先使用自定义尺寸
  if (customWidth && customHeight) {
    // 确保尺寸在合理范围内，不要超过Pollinations.ai支持的最大尺寸
    width = Math.min(Math.max(parseInt(String(customWidth), 10) || 512, 64), 1280);
    height = Math.min(Math.max(parseInt(String(customHeight), 10) || 512, 64), 1280);
    sizeMethod = 'custom';
    console.log(`使用自定义尺寸: ${width}x${height}`);
  } 
  // 否则，如果提供了分辨率，使用分辨率
  else if (resolution && typeof resolution === 'string' && resolution.includes('x')) {
    const [resWidth, resHeight] = resolution.split('x').map(dim => parseInt(dim, 10));
    if (resWidth && resHeight && !isNaN(resWidth) && !isNaN(resHeight)) {
      // 确保尺寸在合理范围内
      width = Math.min(Math.max(resWidth, 64), 1280);
      height = Math.min(Math.max(resHeight, 64), 1280);
      sizeMethod = 'resolution';
      console.log(`使用分辨率: ${width}x${height}`);
    }
  } 
  // 否则，根据宽高比设置尺寸
  else if (aspect_ratio && typeof aspect_ratio === 'string' && aspect_ratio.includes(':')) {
    const [w, h] = aspect_ratio.split(':').map(dim => parseInt(dim, 10));
    if (w && h && !isNaN(w) && !isNaN(h)) {
      // 确保宽高比计算正确
      const ratio = w / h;
      
      // 根据宽高比计算尺寸，保持总像素数大致相同
      if (ratio > 1) { // 宽大于高（横向）
        width = 768;
        height = Math.floor(768 / ratio);
      } else { // 高大于宽（纵向）
        height = 768;
        width = Math.floor(768 * ratio);
      }
      
      // 确保尺寸是32的倍数，这样在某些AI模型中效果更好
      width = Math.floor(width / 32) * 32;
      height = Math.floor(height / 32) * 32;
      
      // 确保尺寸在合理范围内
      width = Math.min(Math.max(width, 64), 1280);
      height = Math.min(Math.max(height, 64), 1280);
      
      sizeMethod = 'aspect_ratio';
      console.log(`使用宽高比 ${aspect_ratio}: ${width}x${height}`);
    }
  } else {
    console.log(`使用默认尺寸: ${width}x${height}`);
  }
  
  // 准备生成的图像数组
  const generatedImages: GeneratedImage[] = [];
  
  try {
    console.log('开始生成图像...');
    
    // 准备风格信息
    let styleText = '';
    let styleParams: string[] = [];
    
    // 处理风格信息
    if (style) {
      const styleId = style.id;
      // 首先使用风格的描述或名称作为基础
      styleText = style.description || style.name;
      console.log(`选择的风格: ${styleId} - ${styleText}`);
      
      // 根据风格类型准备不同的提示词和参数
      switch (styleId) {
        // 3D类风格
        case '3d':
          styleText = '3D rendering, three-dimensional, realistic lighting, strong depth, detailed textures, 3D modeling';
          styleParams = ['style=3d', 'render_style=3d', 'quality=high'];
          break;
        case 'isometric':
          styleText = 'isometric view, 3D isometric, 45-degree angle, geometric precision';
          styleParams = ['style=isometric', 'render_style=3d'];
          break;
        case 'voxel':
          styleText = 'voxel art, cubic elements, 3D pixels, minecraft style';
          styleParams = ['style=voxel', 'render_style=3d'];
          break;
        case 'low-poly':
          styleText = 'low poly 3D, geometric faces, triangular surfaces, simplified 3D model';
          styleParams = ['style=low-poly', 'render_style=3d'];
          break;
          
        // 绘画类风格
        case 'watercolor':
          styleText = 'watercolor painting, soft edges, fluid color transitions, transparent washes, artistic brushstrokes';
          styleParams = ['style=watercolor', 'artistic=true'];
          break;
        case 'oil':
          styleText = 'oil painting, textured brushstrokes, rich colors, canvas texture, traditional painting technique';
          styleParams = ['style=oil-painting', 'artistic=true'];
          break;
        case 'pencil':
          styleText = 'pencil drawing, graphite, detailed linework, shading, sketched, hand-drawn';
          styleParams = ['style=pencil-drawing', 'artistic=true'];
          break;
        case 'guohua':
          styleText = 'Chinese painting, ink wash, traditional guohua style, elegant brushwork, minimalist composition';
          styleParams = ['style=chinese-painting', 'artistic=true'];
          break;
        case 'ukiyo-e':
          styleText = 'ukiyo-e style, Japanese woodblock print, flat colors, bold outlines, traditional Japanese art';
          styleParams = ['style=ukiyo-e', 'artistic=true'];
          break;
          
        // 数字艺术类风格
        case 'digital-art':
          styleText = 'digital art, digital painting, professional illustration, detailed, vibrant colors';
          styleParams = ['style=digital-art'];
          break;
        case 'pixel':
          styleText = 'pixel art, 8-bit style, retro game aesthetic, limited color palette, blocky pixels';
          styleParams = ['style=pixel-art'];
          break;
        case 'cyberpunk':
          styleText = 'cyberpunk style, neon lights, futuristic city, high tech low life, dystopian future';
          styleParams = ['style=cyberpunk'];
          break;
        case 'neon':
          styleText = 'neon lighting, vibrant glowing colors, dark background, neon signs, electric atmosphere';
          styleParams = ['style=neon'];
          break;
          
        // 动画类风格
        case 'anime':
          styleText = 'anime style, Japanese animation, clean lines, expressive eyes, vibrant colors';
          styleParams = ['style=anime'];
          break;
        case 'cartoon':
          styleText = 'cartoon style, simplified shapes, bold outlines, exaggerated features, colorful';
          styleParams = ['style=cartoon'];
          break;
        case 'disney':
          styleText = 'Disney animation style, magical, whimsical, expressive characters, smooth animation';
          styleParams = ['style=disney'];
          break;
        case 'ghibli':
          styleText = 'Studio Ghibli style, Miyazaki inspired, whimsical, detailed backgrounds, soft colors';
          styleParams = ['style=ghibli'];
          break;
        case 'pixar':
          styleText = 'Pixar style, 3D animation, expressive characters, emotional storytelling, detailed textures';
          styleParams = ['style=pixar'];
          break;
          
        // 写实类风格
        case 'realistic':
          styleText = 'realistic style, photorealistic, detailed, true to life, natural lighting';
          styleParams = ['style=realistic', 'quality=high'];
          break;
        case 'hyperrealism':
          styleText = 'hyperrealistic, photorealistic, extremely detailed, ultra high definition, indistinguishable from reality';
          styleParams = ['style=hyperrealistic', 'quality=high'];
          break;
        case 'photography':
          styleText = 'professional photography, high resolution, perfect lighting, sharp focus, detailed';
          styleParams = ['style=photography', 'quality=high'];
          break;
          
        // 艺术流派风格
        case 'impressionism':
        case 'claude-monet':
          styleText = 'impressionist painting, loose brushwork, light and color, outdoor scenes, Monet style';
          styleParams = ['style=impressionism', 'artistic=true'];
          break;
        case 'surrealism':
        case 'salvador-dali':
          styleText = 'surrealist art, dreamlike imagery, impossible scenes, symbolic elements, Salvador Dali style';
          styleParams = ['style=surrealism', 'artistic=true'];
          break;
        case 'cubism':
        case 'pablo-picasso':
          styleText = 'cubist art, geometric shapes, multiple perspectives, fragmented forms, Picasso style';
          styleParams = ['style=cubism', 'artistic=true'];
          break;
        case 'pop-art':
          styleText = 'pop art style, bold colors, commercial imagery, comic book style, Andy Warhol inspired';
          styleParams = ['style=pop-art', 'artistic=true'];
          break;
        case 'abstract':
          styleText = 'abstract art, non-representational, shapes and colors, expressive, conceptual';
          styleParams = ['style=abstract', 'artistic=true'];
          break;
        case 'minimalism':
          styleText = 'minimalist style, simple forms, limited color palette, clean lines, uncluttered';
          styleParams = ['style=minimalism'];
          break;
        case 'van-gogh':
          styleText = 'Van Gogh style, swirling brushstrokes, intense colors, emotional, post-impressionist';
          styleParams = ['style=van-gogh', 'artistic=true'];
          break;
          
        // 其他风格
        case 'steampunk':
          styleText = 'steampunk aesthetic, Victorian era, brass and copper, steam-powered machinery, gears and cogs';
          styleParams = ['style=steampunk'];
          break;
        case 'sci-fi':
          styleText = 'science fiction, futuristic technology, space themes, advanced civilization, otherworldly';
          styleParams = ['style=sci-fi'];
          break;
        case 'fantasy':
          styleText = 'fantasy art, magical world, mythical creatures, enchanted landscapes, heroic adventure';
          styleParams = ['style=fantasy'];
          break;
        case 'horror':
          styleText = 'horror style, dark and eerie, creepy atmosphere, suspenseful, macabre elements';
          styleParams = ['style=horror'];
          break;
        case 'vintage':
          styleText = 'vintage aesthetic, retro style, aged appearance, nostalgic, old-fashioned';
          styleParams = ['style=vintage'];
          break;
        case 'graffiti':
          styleText = 'graffiti art, street art, spray paint style, urban aesthetic, bold colors and lines';
          styleParams = ['style=graffiti'];
          break;
        default:
          // 如果没有特定的风格描述，使用风格ID作为参数，保留原始styleText
          console.log(`使用默认风格处理: ${styleId}`);
          styleParams = [`style=${styleId}`];
          break;
      }
    }
    
    // 直接使用原始提示词，不做任何修改或增强
    let fullPrompt = prompt;
    
    // 如果有风格文本，添加到提示词中
    if (styleText) {
      fullPrompt += `, ${styleText}`;
      console.log(`添加风格文本到提示词: ${styleText}`);
    }
    
    // 完全使用用户提供的否定提示词，不添加任何默认内容，确保用户输入的否定词被完全尊重
    let negativePromptText = negative_prompt || '';
    
    // 如果用户提供了否定提示词，记录日志以便调试
    if (negative_prompt) {
      console.log('用户提供了否定提示词，将严格使用:', negative_prompt);
    } else {
      // 如果用户没有提供否定提示词，使用默认的否定提示词
      negativePromptText = 'bad anatomy, extra limbs, poorly drawn, deformed, mutation, blurry, distorted, disfigured, low quality, pixelated, watermark, signature, text';
      console.log('用户未提供否定提示词，使用默认否定提示词');
    }
    
    console.log('最终提示词:', fullPrompt);
    console.log('否定提示词:', negativePromptText);
    
    // 完全重构批量生成逻辑，确保一致性
    try {
      // 第一步：先生成第一张图片
      console.log('开始生成第一张图片...');
      
      // 使用基础URL参数法构建请求URL
      // 严格按照用户提供的示例：https://image.pollinations.ai/prompt/{正面提示词}?negative_prompt={负面内容}&model=flux&nologo=true
      
      // 先编码正面提示词
      const encodedPrompt = encodeURIComponent(fullPrompt);
      console.log('编码后的正面提示词:', encodedPrompt);
      
      // 准备参数字符串，严格按照示例格式
      let queryString = '';
      
      // 添加负面提示词 - 最高优先级
      if (negativePromptText) {
        queryString += `negative_prompt=${encodeURIComponent(negativePromptText)}`;
        console.log('添加负面提示词 (原始形式):', negativePromptText);
        console.log('添加负面提示词 (编码后):', encodeURIComponent(negativePromptText));
      }
      
      // 添加模型参数
      queryString += queryString ? '&' : '';
      queryString += `model=${model || 'flux'}`;  // 默认使用flux而非stable-diffusion
      
      // 添加移除水印参数
      queryString += '&nologo=true';
      
      // 添加其他参数
      queryString += `&width=${width}&height=${height}`;
      
      // 添加风格参数
      for (const styleParam of styleParams) {
        queryString += `&${styleParam}`;
      }
      
      // 使用提供的种子或生成随机种子
      let firstImageSeed: number;
      if (seed && !isNaN(parseInt(seed, 10))) {
        firstImageSeed = parseInt(seed, 10);
        console.log(`使用用户提供的种子值: ${firstImageSeed}`);
      } else {
        firstImageSeed = Math.floor(Math.random() * 1000000000);
        console.log(`生成随机种子值: ${firstImageSeed}`);
      }
      
      // 添加种子值参数
      queryString += `&seed=${firstImageSeed}`;
      
      // 添加缓存破坏参数
      queryString += `&cachebuster=${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
      
      // 构建完整URL - 严格按照示例格式
      const baseUrl = BASE_URL;
      const firstImageUrl = `${baseUrl}/prompt/${encodedPrompt}?${queryString}`;
      
      console.log('第一张图片完整URL:', firstImageUrl);
      console.log('负面提示词在URL中的位置:', firstImageUrl.indexOf('negative_prompt'));
      
      // 尝试加载第一张图片
      let firstImageSuccessful = false;
      let firstImageFinalUrl = '';
      let retryFirstImage = 0;
      const maxFirstImageRetries = 5; // 增加第一张图片的最大重试次数
      
      // 增强的图片加载函数
      const loadImageWithRetry = async (imageUrl: string, retryCount: number = 0, maxRetries: number = 3, timeoutMs: number = 60000): Promise<string> => {
        console.log(`尝试加载图片 (尝试 ${retryCount + 1}/${maxRetries + 1}): ${imageUrl}`);
        
        try {
          // 先尝试使用代理
          const proxiedUrl = wrapWithCorsProxy(imageUrl);
          
          const img = new Image();
          img.crossOrigin = "anonymous";
          
          const result = await new Promise<string>((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.warn(`图片加载超时 (${timeoutMs}ms)`);
              reject(new Error('TIMEOUT'));
            }, timeoutMs);
            
            img.onload = () => {
              clearTimeout(timeout);
              console.log('图片加载成功');
              resolve(imageUrl); // 返回原始URL，而不是代理URL
            };
            
            img.onerror = (e) => {
              clearTimeout(timeout);
              console.error('图片加载失败:', e);
              reject(new Error('LOAD_ERROR'));
            };
            
            img.src = proxiedUrl;
          });
          
          return result;
        } catch (error) {
          // 如果代理加载失败，尝试直接加载
          if (retryCount < maxRetries) {
            console.log(`重试加载图片 (${retryCount + 1}/${maxRetries})...`);
            
            // 在重试前切换到下一个代理
            switchToNextProxy();
            
            // 增加延迟，避免过快重试
            await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
            
            // 如果是最后一次重试，尝试直接加载而不是通过代理
            if (retryCount === maxRetries - 1) {
              try {
                console.log('尝试直接加载图片（不使用代理）...');
                const img = new Image();
                
                const directResult = await new Promise<string>((resolve, reject) => {
                  const timeout = setTimeout(() => {
                    console.warn(`直接加载图片超时 (${timeoutMs}ms)`);
                    reject(new Error('DIRECT_TIMEOUT'));
                  }, timeoutMs);
                  
                  img.onload = () => {
                    clearTimeout(timeout);
                    console.log('直接加载图片成功');
                    resolve(imageUrl);
                  };
                  
                  img.onerror = (e) => {
                    clearTimeout(timeout);
                    console.error('直接加载图片失败:', e);
                    reject(new Error('DIRECT_LOAD_ERROR'));
                  };
                  
                  img.src = imageUrl;
                });
                
                return directResult;
              } catch (directError) {
                console.error('直接加载也失败:', directError);
              }
            }
            
            // 递归重试
            return loadImageWithRetry(imageUrl, retryCount + 1, maxRetries, timeoutMs);
          }
          
          // 如果所有重试都失败，尝试使用简化URL
          console.log('所有重试都失败，尝试使用简化URL...');
          const urlObj = new URL(imageUrl);
          const pathWithPrompt = urlObj.pathname;
          
          // 构建简化URL，保留关键参数
          let simpleUrl = `${baseUrl}${pathWithPrompt}?width=${width}&height=${height}&seed=${firstImageSeed}&nologo=true`;
          
          // 如果有负面提示词，也添加到简化URL中
          if (negativePromptText) {
            simpleUrl += `&negative_prompt=${encodeURIComponent(negativePromptText)}`;
          }
          
          try {
            const proxiedSimpleUrl = wrapWithCorsProxy(simpleUrl);
            
            const img = new Image();
            img.crossOrigin = "anonymous";
            
            const simpleResult = await new Promise<string>((resolve, reject) => {
              const timeout = setTimeout(() => {
                console.warn(`简化URL加载超时 (${timeoutMs}ms)`);
                reject(new Error('SIMPLE_TIMEOUT'));
              }, timeoutMs);
              
              img.onload = () => {
                clearTimeout(timeout);
                console.log('简化URL加载成功');
                resolve(simpleUrl);
              };
              
              img.onerror = (e) => {
                clearTimeout(timeout);
                console.error('简化URL加载失败:', e);
                reject(new Error('SIMPLE_LOAD_ERROR'));
              };
              
              img.src = proxiedSimpleUrl;
            });
            
            return simpleResult;
          } catch (simpleError) {
            console.error('简化URL也加载失败:', simpleError);
            throw new Error('ALL_LOADING_STRATEGIES_FAILED');
          }
        }
      };
      
      // 使用增强的加载函数加载第一张图片
      while (retryFirstImage < maxFirstImageRetries && !firstImageSuccessful) {
        try {
          if (retryFirstImage > 0) {
            console.log(`第一张图片加载失败，进行第 ${retryFirstImage} 次重试...`);
            await new Promise(resolve => setTimeout(resolve, 3000 * retryFirstImage));
          }
          
          firstImageFinalUrl = await loadImageWithRetry(firstImageUrl, 0, 3, 60000);
          firstImageSuccessful = true;
        } catch (error) {
          console.error(`第一张图片第 ${retryFirstImage + 1} 次尝试失败:`, error);
          retryFirstImage++;
          
          // 如果是最后一次重试，尝试更简单的URL
          if (retryFirstImage === maxFirstImageRetries - 1) {
            try {
              console.log('尝试使用最简单的URL生成第一张图片...');
              const verySimpleUrl = `${baseUrl}/prompt/${encodedPrompt}?width=${width}&height=${height}&seed=${firstImageSeed}&nologo=true`;
              firstImageFinalUrl = await loadImageWithRetry(verySimpleUrl, 0, 2, 60000);
              firstImageSuccessful = true;
            } catch (simpleError) {
              console.error('最简单URL也加载失败:', simpleError);
            }
          }
        }
      }
      
      // 如果第一张图片加载成功，则添加到结果集
      if (firstImageSuccessful && firstImageFinalUrl) {
        const firstImage: GeneratedImage = {
          id: `img_${Date.now()}_${Math.floor(Math.random() * 1000)}_0`,
          url: firstImageFinalUrl,
          prompt: prompt,
          negative_prompt: negative_prompt || '',
          model: model,
          created_at: new Date().toISOString(),
          aspect_ratio: customWidth && customHeight ? '自定义' : aspect_ratio,
          quality: quality,
          seed: firstImageSeed,
          style: style,
          is_fallback: false,
          resolution: `${width}x${height}`
        };
        
        generatedImages.push(firstImage);
        console.log('第一张图片成功添加到结果集');
        
        // 第二步：如果需要生成多张图片，使用第一张图片的URL作为精确模板
        if (count > 1) {
          console.log(`需要生成额外的 ${count - 1} 张图片`);
          
          // 不使用第一张图片的URL结构，而是重新构建完全相同的URL
          // 这样可以确保所有图片使用相同的提示词和参数
          
          // 使用相同的基础URL参数法构建后续图片URL
          // 严格按照示例格式构建后续图片URL
          
          // 逐一生成剩余的图片
          for (let i = 1; i < count; i++) {
            try {
              // 在每次生成图片之前添加延迟，避免API限制
              console.log(`等待8秒后生成第 ${i + 1} 张图片...`); // 增加等待时间
              await new Promise(resolve => setTimeout(resolve, 8000));
              
              // 为当前图片创建新的种子值 - 在第一张图片的种子值基础上递增100
              const currentSeed = firstImageSeed + (i * 100);
              console.log(`第 ${i + 1} 张图片种子值: ${currentSeed} (第一张 ${firstImageSeed} + ${i * 100})`);
              
              // 为当前图片创建新的查询字符串，基于第一张图片的格式
              let currentQueryString = '';
              
              // 添加负面提示词 - 最高优先级
              if (negativePromptText) {
                currentQueryString += `negative_prompt=${encodeURIComponent(negativePromptText)}`;
                console.log(`第 ${i + 1} 张图片使用负面提示词: ${negativePromptText}`);
              }
              
              // 添加模型参数
              currentQueryString += currentQueryString ? '&' : '';
              currentQueryString += `model=${model || 'flux'}`;
              
              // 添加移除水印参数
              currentQueryString += '&nologo=true';
              
              // 添加其他参数
              currentQueryString += `&width=${width}&height=${height}`;
              
              // 添加风格参数
              for (const styleParam of styleParams) {
                currentQueryString += `&${styleParam}`;
              }
              
              // 添加种子值参数
              currentQueryString += `&seed=${currentSeed}`;
              
              // 更新缓存破坏参数
              currentQueryString += `&cachebuster=${Date.now()}_${Math.floor(Math.random() * 1000000)}_${i}`;
              
              // 构建完整URL - 严格按照示例格式
              const currentImageUrl = `${baseUrl}/prompt/${encodedPrompt}?${currentQueryString}`;
              console.log(`第 ${i + 1} 张图片URL:`, currentImageUrl);
              console.log(`负面提示词在URL中的位置:`, currentImageUrl.indexOf('negative_prompt'));
              
              // 尝试加载当前图片，使用增强的加载函数
              let currentImageSuccessful = false;
              let currentImageFinalUrl = '';
              
              try {
                currentImageFinalUrl = await loadImageWithRetry(currentImageUrl, 0, 4, 60000); // 增加重试次数和超时时间
                currentImageSuccessful = true;
              } catch (loadError) {
                console.error(`加载第 ${i + 1} 张图片失败，尝试备用方法:`, loadError);
                
                // 备用方法：使用更简单的URL，但保留关键参数
                try {
                  // 使用当前图片的模型参数
                  const modelParam = `model=${model || 'stable-diffusion'}`;
                  
                  // 构建备用URL的查询字符串
                  let backupQueryString = `width=${width}&height=${height}&${modelParam}&seed=${currentSeed}&nologo=true`;
                  
                  // 如果有负面提示词，也添加到备用URL中
                  if (negativePromptText) {
                    backupQueryString += `&negative_prompt=${encodeURIComponent(negativePromptText)}`;
                  }
                  
                  const backupUrl = `${baseUrl}/prompt/${encodedPrompt}?${backupQueryString}`;
                  console.log(`尝试使用备用URL加载第 ${i + 1} 张图片:`, backupUrl);
                  
                  currentImageFinalUrl = await loadImageWithRetry(backupUrl, 0, 3, 60000);
                  currentImageSuccessful = true;
                } catch (backupError) {
                  console.error(`备用方法也失败:`, backupError);
                }
              }
              
              // 如果当前图片加载成功，添加到结果集
              if (currentImageSuccessful && currentImageFinalUrl) {
                const currentImage: GeneratedImage = {
                  id: `img_${Date.now()}_${Math.floor(Math.random() * 1000)}_${i}`,
                  url: currentImageFinalUrl,
                  prompt: prompt,
                  negative_prompt: negative_prompt || '',
                  model: model,
                  created_at: new Date().toISOString(),
                  aspect_ratio: customWidth && customHeight ? '自定义' : aspect_ratio,
                  quality: quality,
                  seed: currentSeed,
                  style: style,
                  is_fallback: false,
                  resolution: `${width}x${height}`
                };
                
                generatedImages.push(currentImage);
                console.log(`第 ${i + 1} 张图片成功添加到结果集`);
              } else {
                console.warn(`第 ${i + 1} 张图片在多次尝试后仍然加载失败，继续下一张`);
              }
            } catch (error) {
              console.error(`处理第 ${i + 1} 张图片时出错:`, error);
              // 继续尝试下一张图片
            }
          }
        }
      } else {
        throw new Error('FIRST_IMAGE_FAILED');
      }
      
      // 如果所有图片都生成失败，抛出错误
      if (generatedImages.length === 0) {
        throw new Error('ALL_IMAGES_FAILED');
      }
      
      // 如果只成功生成了部分图片，记录警告但仍返回成功的图片
      if (generatedImages.length < count) {
        console.warn(`请求生成 ${count} 张图片，但只成功生成了 ${generatedImages.length} 张`);
      }
      
      return generatedImages;
    } catch (error) {
      console.error('生成图像失败:', error);
      
      // 检查是否特定错误类型
      if (error instanceof Error) {
        if (error.message === 'API_UNAVAILABLE') {
          throw new Error('API_UNAVAILABLE');
        } else if (error.message === 'NETWORK_UNAVAILABLE') {
          throw new Error('NETWORK_UNAVAILABLE');
        } else if (error.message === 'IMAGE_LOAD_FAILED') {
          throw new Error('IMAGE_LOAD_FAILED');
        } else if (error.message === 'API_REQUEST_FAILED') {
          throw new Error('API_REQUEST_FAILED');
        } else if (error.message === 'ALL_IMAGES_FAILED') {
          throw new Error('ALL_IMAGES_FAILED');
        } else if (error.message === 'FIRST_IMAGE_FAILED') {
          throw new Error('FIRST_IMAGE_FAILED');
        }
      }
      
      // 重新抛出错误
      throw error;
    }
  } catch (error) {
    console.error('生成图像失败:', error);
    throw error;
  }
};

// 保存图像到本地
export const saveImageToLocal = async (imageUrl: string, filename: string): Promise<string> => {
  try {
    // 在浏览器环境中，我们可以使用Blob和URL.createObjectURL
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    // 创建下载链接
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'ai-painting.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    // 释放URL对象
    URL.revokeObjectURL(url);
    
    return filename;
  } catch (error) {
    console.error('保存图像失败:', error);
    throw error;
  }
};
